import { motion } from "framer-motion";
import { ClipboardCheck, HardHat, KeyRound } from "lucide-react";

const CRAFT_IMG =
  "https://images.unsplash.com/photo-1704742950992-9815a104820c?crop=entropy&cs=srgb&fm=jpg&q=85";

const STEPS = [
  {
    icon: ClipboardCheck,
    title: "Assess & Mitigate",
    desc: "We stop the damage, document everything for your insurance, and map the full path home.",
  },
  {
    icon: HardHat,
    title: "Restore & Rebuild",
    desc: "Crews dry, clean, and decontaminate — then our craftsmen rebuild framing, drywall, roofing, and finishes.",
  },
  {
    icon: KeyRound,
    title: "Welcome Home",
    desc: "A final walkthrough with you, down to the last detail. Your house, put back together.",
  },
];

export default function Rebuild() {
  return (
    <section className="relative overflow-hidden py-24 lg:py-32" data-testid="rebuild-section">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_left,rgba(139,92,246,0.08),transparent_55%)]" />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.215, 0.61, 0.355, 1] }}
          className="relative"
        >
          <div className="absolute -inset-3 rounded-3xl border border-purple-500/25" />
          <div className="relative overflow-hidden rounded-2xl">
            <img
              src={CRAFT_IMG}
              alt="Craftsman rebuilding interior wood framing"
              className="aspect-[4/5] w-full object-cover"
              data-testid="rebuild-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#08080A]/60 via-transparent to-transparent" />
          </div>
          <div className="font-mono-data absolute -bottom-5 left-6 rounded-full border border-purple-500/30 bg-[#0B0B10] px-5 py-2.5 text-[10px] uppercase tracking-[0.25em] text-purple-300 shadow-[0_0_30px_rgba(0,0,0,0.6)]">
            Mitigation → Rebuild → Home
          </div>
        </motion.div>

        <div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.215, 0.61, 0.355, 1] }}
          >
            <p className="font-mono-data mb-4 text-xs uppercase tracking-[0.35em] text-purple-400">
              The Part Others Skip
            </p>
            <h2 className="font-serif-display text-3xl font-medium leading-tight tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
              Then we put your house <span className="italic text-purple-400">back together.</span>
            </h2>
            <p className="mt-6 text-base font-light leading-relaxed text-slate-400">
              Most companies leave after the cleanup and hand you a list of contractors. We believe
              restoration is not finished until your home is whole — so rebuilding is part of every
              job we take on.
            </p>
          </motion.div>

          <div className="mt-10 space-y-8">
            {STEPS.map((step, i) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.15 + i * 0.12, duration: 0.7, ease: [0.215, 0.61, 0.355, 1] }}
                className="flex gap-5"
                data-testid={`rebuild-step-${i + 1}`}
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-purple-500/30 bg-purple-500/10 text-purple-300">
                  <step.icon className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-serif-display text-xl font-medium text-white">
                    <span className="font-mono-data mr-3 text-xs tracking-widest text-purple-500">
                      0{i + 1}
                    </span>
                    {step.title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
